import { HttpClient, HttpHeaders, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, catchError, map, of, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import { UnknownErrorException } from '../core/exceptions/unknown-error-exception';
import { ValidationException } from '../core/exceptions/validation-exception';

import { NewsletterTemplateModel } from '../models/newsletter-template-model';
import { CreateNewsletterTemplateModel } from '../models/create-newsletter-template-model';
import { UpdateNewsletterTemplateModel } from '../models/update-newsletter-template-model';
import { Page } from '../models/paging/page-model';
import { formatDate } from '@angular/common';

export class CreateNewsletterTemplateApiModel {
  name: string;
  htmlContent: string;
  cssContent: string;
  templateContent: string;
  projectData : string;
}

export class UpdateNewsletterTemplateApiModel {
  name: string;
  htmlContent: string;
  cssContent: string;
  templateContent: string;
  projectData : string;
}

@Injectable({
  providedIn: 'root'
})
export class NewsletterTemplateService {
  constructor(private http: HttpClient) { }

  getNewsletterTemplates(companyId: string, currentPage: number, pageSize: number): Observable<Page<NewsletterTemplateModel>> {
    let httpParams = new HttpParams();
    httpParams = httpParams.append("page", currentPage.toString());
    httpParams = httpParams.append("size", pageSize.toString());

    const httpOptions = {
      headers: new HttpHeaders({
        ContentType: 'application/json',
      }),
      params: httpParams
    };

    const url = environment.apiUrl + '/api/partner/companies/' + companyId + '/newsletters/templates';
    const result = this.http.get<Page<NewsletterTemplateModel>>(url, httpOptions);
    return result.pipe(
      catchError(
        this.handleError<Page<NewsletterTemplateModel>>('getNewsletterTemplates', null)
      )
    );
  }

  getNewsletterTemplate(companyId: string, newsletterTemplateId: string): Observable<NewsletterTemplateModel> {
    const httpOptions = {
      headers: new HttpHeaders({
        ContentType: 'application/json',
      }),
    };

    const url = environment.apiUrl + '/api/partner/companies/' + companyId + '/newsletters/templates/' + newsletterTemplateId;
    const result = this.http.get<NewsletterTemplateModel>(url, httpOptions);
    return result.pipe(
      map(data => this.mapFromData(data)),
      catchError(
        this.handleError<NewsletterTemplateModel>('getNewsletterTemplate', null)
      )
    );
  }

  createNewsletterTemplate(companyId: string, createNewsletterTemplate: CreateNewsletterTemplateModel): Observable<NewsletterTemplateModel> {
    const httpOptions = {
      headers: new HttpHeaders({
        ContentType: 'application/json',
      }),
    };

    const createNewsletterTemplateApiModel = new CreateNewsletterTemplateApiModel();
    createNewsletterTemplateApiModel.name = createNewsletterTemplate.name;
    createNewsletterTemplateApiModel.htmlContent = createNewsletterTemplate.htmlContent;
    createNewsletterTemplateApiModel.cssContent = createNewsletterTemplate.cssContent;
    createNewsletterTemplateApiModel.templateContent = createNewsletterTemplate.templateContent;
    createNewsletterTemplateApiModel.projectData = JSON.stringify(createNewsletterTemplate.projectData);

    const url = environment.apiUrl + '/api/partner/companies/' + companyId + '/newsletters/templates';
    const result = this.http.post<NewsletterTemplateModel>(url, createNewsletterTemplateApiModel, httpOptions);
    return result.pipe(
      map(data => {
        return this.mapFromData(data);
      }),
      catchError((response: HttpErrorResponse) => {
        if (response.status == 400) {
          if (response.error.violations) {
            const validationException = new ValidationException(response.error.violations);
            return throwError(() => validationException);
          }
          return throwError(() => new UnknownErrorException());
        }
        return throwError(() => this.handleError('createNewsletterTemplate', null));
      })
    );
  }

  updateNewsletterTemplate(companyId: string, updateNewsletterTemplate: UpdateNewsletterTemplateModel): Observable<NewsletterTemplateModel> {
    const httpOptions = {
      headers: new HttpHeaders({
        ContentType: 'application/json',
      }),
    };

    const updateNewsletterTemplateApiModel = new UpdateNewsletterTemplateApiModel();
    updateNewsletterTemplateApiModel.name = updateNewsletterTemplate.name;
    updateNewsletterTemplateApiModel.htmlContent = updateNewsletterTemplate.htmlContent;
    updateNewsletterTemplateApiModel.cssContent = updateNewsletterTemplate.cssContent;
    updateNewsletterTemplateApiModel.templateContent = updateNewsletterTemplate.templateContent;
    updateNewsletterTemplateApiModel.projectData = JSON.stringify(updateNewsletterTemplate.projectData);

    const url = environment.apiUrl + '/api/partner/companies/' + companyId + '/newsletters/templates/' + updateNewsletterTemplate.newsletterTemplateId;
    const result = this.http.put<NewsletterTemplateModel>(url, updateNewsletterTemplateApiModel, httpOptions);
    return result.pipe(
      map(data => {
        return this.mapFromData(data);
      }),
      catchError((response: HttpErrorResponse) => {
        if (response.status == 400) {
          if (response.error.violations) {
            const validationException = new ValidationException(response.error.violations);
            return throwError(() => validationException);
          }
          return throwError(() => new UnknownErrorException());
        }
        return throwError(() => this.handleError('updateNewsletterTemplate', null));
      })
    );
  }

  deleteNewsletterTemplate(companyId: string, newsletterTemplateId: string): Observable<any> {
    const httpOptions = {
      headers: new HttpHeaders({
        ContentType: 'application/json',
      }),
    };

    const url = environment.apiUrl + '/api/partner/companies/' + companyId + '/newsletters/templates/' + newsletterTemplateId;
    const result = this.http.delete(url, httpOptions);
    return result.pipe(catchError(this.handleError('deleteNewsletterTemplate', null)));
  }

  private mapFromData(data: NewsletterTemplateModel): NewsletterTemplateModel {
    const newsletterTemplate = new NewsletterTemplateModel();
    newsletterTemplate.name = data.name;
    newsletterTemplate.htmlContent = data.htmlContent;
    newsletterTemplate.cssContent = data.cssContent;
    newsletterTemplate.templateContent = data.templateContent;
    newsletterTemplate.projectData = JSON.parse(data.projectData);
    newsletterTemplate.createdDate = data.createdDate;
    newsletterTemplate.updatedDate = data.updatedDate;
    return newsletterTemplate;
  }
  /**
   * Handle Http operation that failed.
   * Let the app continue.
   * @param operation - name of the operation that failed
   * @param result - optional value to return as the observable result
   */
  private handleError<T>(operation = 'operation', result?: T) {
    return (error: any): Observable<T> => {

      // TODO: send the error to remote logging infrastructure
      console.error(error); // log to console instead

      // TODO: better job of transforming error for user consumption
      // this.log(`${operation} failed: ${error.message}`);

      // Let the app keep running by returning an empty result.
      return of(result);
    };
  }
}